import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { sendWithRetry, type FeedbackKind } from '@/lib/send-feedback'

export interface FeedbackInput {
  kind: FeedbackKind
  text: string
  /** The same for every try of the same words, so the function files them once (`reportId()`). */
  id: string
  /** Short labelled values, sent only when "Include app details" is ticked. */
  details?: Record<string, string>
}

const call = httpsCallable<FeedbackInput, { sent: true }>(functions, 'sendFeedback')

/** Failures that may only be the connection: a request cut off by the phone, or a function that did not answer. */
const RETRYABLE = ['functions/internal', 'functions/unavailable', 'functions/deadline-exceeded', 'functions/unknown']

/** Resolves once the app is in front, since a phone cuts off requests from an app in the background. */
function whenVisible(): Promise<void> {
  if (document.visibilityState === 'visible') return Promise.resolve()
  return new Promise((resolve) => {
    const onChange = () => {
      if (document.visibilityState !== 'visible') return
      document.removeEventListener('visibilitychange', onChange)
      resolve()
    }
    document.addEventListener('visibilitychange', onChange)
  })
}

/** Sends a bug report or an idea; the sendFeedback function files it as a GitHub issue. */
export async function sendFeedback(input: FeedbackInput): Promise<void> {
  await sendWithRetry(() => call(input), {
    retryable: (e) => RETRYABLE.includes((e as { code?: string }).code ?? ''),
    ready: whenVisible,
  })
}
